/**
 * Storage Manager
 * Provides quota monitoring, multi-tab coordination, and locking for IndexedDB operations.
 *
 * Features:
 * - Quota monitoring with automatic cleanup when >80% full
 * - BroadcastChannel for multi-tab coordination
 * - Mutex/lock pattern for critical operations
 * - Error recovery and retry logic
 */

// ============================================
// Types
// ============================================

export interface StorageQuota {
  usage: number;
  quota: number;
  percentUsed: number;
  available: number;
}

export interface StorageEvent {
  type: 'write' | 'delete' | 'clear' | 'sync';
  store: string;
  key?: string;
  timestamp: number;
  tabId: string;
}

type LockReleaser = () => void;

// ============================================
// Constants
// ============================================

const QUOTA_WARNING_THRESHOLD = 0.8; // 80%
const QUOTA_CRITICAL_THRESHOLD = 0.95; // 95%
const LOCK_TIMEOUT_MS = 30000; // 30 seconds
const CHANNEL_NAME = 'engraving-pwa-storage';

// Generate unique tab ID
const TAB_ID = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// ============================================
// Quota Management
// ============================================

/**
 * Get current storage quota and usage
 */
export async function getStorageQuota(): Promise<StorageQuota> {
  if (!navigator.storage?.estimate) {
    // Fallback for browsers without StorageManager API
    return {
      usage: 0,
      quota: 50 * 1024 * 1024, // Assume 50MB default
      percentUsed: 0,
      available: 50 * 1024 * 1024,
    };
  }

  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 50 * 1024 * 1024;
    const percentUsed = quota > 0 ? usage / quota : 0;
    const available = quota - usage;

    return { usage, quota, percentUsed, available };
  } catch (error) {
    console.error('[StorageManager] Failed to get quota:', error);
    return {
      usage: 0,
      quota: 50 * 1024 * 1024,
      percentUsed: 0,
      available: 50 * 1024 * 1024,
    };
  }
}

/**
 * Check if storage is running low
 */
export async function isStorageLow(): Promise<boolean> {
  const quota = await getStorageQuota();
  return quota.percentUsed >= QUOTA_WARNING_THRESHOLD;
}

/**
 * Check if storage is critically low
 */
export async function isStorageCritical(): Promise<boolean> {
  const quota = await getStorageQuota();
  return quota.percentUsed >= QUOTA_CRITICAL_THRESHOLD;
}

/**
 * Request persistent storage (prevents browser from evicting data)
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) {
    return false;
  }

  try {
    const isPersisted = await navigator.storage.persisted();
    if (isPersisted) {
      return true;
    }

    return await navigator.storage.persist();
  } catch (error) {
    console.error('[StorageManager] Failed to request persistent storage:', error);
    return false;
  }
}

// ============================================
// Multi-Tab Coordination via BroadcastChannel
// ============================================

let broadcastChannel: BroadcastChannel | null = null;
const eventListeners: Set<(event: StorageEvent) => void> = new Set();

/**
 * Initialize the broadcast channel for multi-tab coordination
 */
export function initBroadcastChannel(): void {
  if (broadcastChannel) {
    return;
  }

  try {
    broadcastChannel = new BroadcastChannel(CHANNEL_NAME);

    broadcastChannel.onmessage = (event: MessageEvent<StorageEvent>) => {
      // Ignore events from this tab
      if (event.data.tabId === TAB_ID) {
        return;
      }

      // Notify all listeners
      eventListeners.forEach((listener) => {
        try {
          listener(event.data);
        } catch (error) {
          console.error('[StorageManager] Event listener error:', error);
        }
      });
    };

    broadcastChannel.onmessageerror = (error) => {
      console.error('[StorageManager] BroadcastChannel error:', error);
    };
  } catch (error) {
    // BroadcastChannel not supported (e.g., older browsers, some Safari versions)
    console.warn('[StorageManager] BroadcastChannel not supported:', error);
  }
}

/**
 * Broadcast a storage event to other tabs
 */
export function broadcastStorageEvent(
  type: StorageEvent['type'],
  store: string,
  key?: string
): void {
  if (!broadcastChannel) {
    return;
  }

  const event: StorageEvent = {
    type,
    store,
    key,
    timestamp: Date.now(),
    tabId: TAB_ID,
  };

  try {
    broadcastChannel.postMessage(event);
  } catch (error) {
    console.error('[StorageManager] Failed to broadcast event:', error);
  }
}

/**
 * Subscribe to storage events from other tabs
 */
export function onStorageEvent(listener: (event: StorageEvent) => void): () => void {
  eventListeners.add(listener);
  return () => {
    eventListeners.delete(listener);
  };
}

/**
 * Close the broadcast channel
 */
export function closeBroadcastChannel(): void {
  if (broadcastChannel) {
    broadcastChannel.close();
    broadcastChannel = null;
  }
  eventListeners.clear();
}

// ============================================
// Mutex/Lock Pattern
// ============================================

const locks: Map<string, { holder: string; acquired: number; waiters: Array<() => void> }> =
  new Map();

/**
 * Acquire a lock for a critical operation
 * @param lockName - Unique name for the lock (e.g., 'cart', 'autosave-front')
 * @param timeoutMs - Maximum time to wait for the lock
 * @returns Function to release the lock
 */
export async function acquireLock(
  lockName: string,
  timeoutMs: number = LOCK_TIMEOUT_MS
): Promise<LockReleaser> {
  const lockId = `${TAB_ID}-${Date.now()}`;

  return new Promise((resolve, reject) => {
    const existing = locks.get(lockName);

    if (!existing) {
      // No existing lock - acquire immediately
      locks.set(lockName, {
        holder: lockId,
        acquired: Date.now(),
        waiters: [],
      });

      resolve(() => releaseLock(lockName, lockId));
      return;
    }

    // Check if existing lock is stale (older than timeout)
    if (Date.now() - existing.acquired > LOCK_TIMEOUT_MS) {
      console.warn(`[StorageManager] Forcing release of stale lock: ${lockName}`);
      locks.set(lockName, {
        holder: lockId,
        acquired: Date.now(),
        waiters: existing.waiters,
      });

      resolve(() => releaseLock(lockName, lockId));
      return;
    }

    // Queue this request
    const timeoutId = setTimeout(() => {
      // Remove from waiters
      const lock = locks.get(lockName);
      if (lock) {
        lock.waiters = lock.waiters.filter((w) => w !== grantLock);
      }
      reject(new Error(`Lock acquisition timed out: ${lockName}`));
    }, timeoutMs);

    const grantLock = () => {
      clearTimeout(timeoutId);
      locks.set(lockName, {
        holder: lockId,
        acquired: Date.now(),
        waiters: locks.get(lockName)?.waiters.filter((w) => w !== grantLock) || [],
      });
      resolve(() => releaseLock(lockName, lockId));
    };

    existing.waiters.push(grantLock);
  });
}

/**
 * Release a lock
 */
function releaseLock(lockName: string, lockId: string): void {
  const lock = locks.get(lockName);

  if (!lock) {
    return;
  }

  // Only release if we hold the lock
  if (lock.holder !== lockId) {
    console.warn(`[StorageManager] Attempted to release lock not held: ${lockName}`);
    return;
  }

  // Grant to next waiter if any
  const nextWaiter = lock.waiters.shift();
  if (nextWaiter) {
    nextWaiter();
  } else {
    locks.delete(lockName);
  }
}

/**
 * Check if a lock is currently held
 */
export function isLocked(lockName: string): boolean {
  const lock = locks.get(lockName);
  if (!lock) {
    return false;
  }

  // Check if stale
  if (Date.now() - lock.acquired > LOCK_TIMEOUT_MS) {
    locks.delete(lockName);
    return false;
  }

  return true;
}

/**
 * Execute a function with a lock
 * Automatically acquires and releases the lock
 */
export async function withLock<T>(
  lockName: string,
  fn: () => Promise<T>,
  timeoutMs?: number
): Promise<T> {
  const release = await acquireLock(lockName, timeoutMs);
  try {
    return await fn();
  } finally {
    release();
  }
}

// ============================================
// Cleanup Utilities
// ============================================

export interface CleanupResult {
  store: string;
  deletedCount: number;
  freedBytes: number;
}

/**
 * Cleanup callback type
 */
export type CleanupStrategy = (db: IDBDatabase) => Promise<CleanupResult[]>;

// Registry of cleanup strategies per store
const cleanupStrategies: Map<string, CleanupStrategy> = new Map();

/**
 * Register a cleanup strategy for a store
 */
export function registerCleanupStrategy(storeName: string, strategy: CleanupStrategy): void {
  cleanupStrategies.set(storeName, strategy);
}

/**
 * Run cleanup on all registered stores
 */
export async function runCleanup(db: IDBDatabase): Promise<CleanupResult[]> {
  const results: CleanupResult[] = [];

  for (const [storeName, strategy] of cleanupStrategies) {
    try {
      const storeResults = await strategy(db);
      results.push(...storeResults);
    } catch (error) {
      console.error(`[StorageManager] Cleanup failed for ${storeName}:`, error);
    }
  }

  return results;
}

/**
 * Check quota and run cleanup if needed
 */
export async function checkAndCleanup(db: IDBDatabase): Promise<CleanupResult[] | null> {
  const quotaInfo = await getStorageQuota();

  if (quotaInfo.percentUsed < QUOTA_WARNING_THRESHOLD) {
    return null; // No cleanup needed
  }

  console.warn(
    `[StorageManager] Storage at ${(quotaInfo.percentUsed * 100).toFixed(1)}% - running cleanup`
  );

  const results = await runCleanup(db);

  // Log results
  const totalFreed = results.reduce((sum, r) => sum + r.freedBytes, 0);
  const totalDeleted = results.reduce((sum, r) => sum + r.deletedCount, 0);
  console.warn(
    `[StorageManager] Cleanup complete: deleted ${totalDeleted} items, freed ~${(totalFreed / 1024).toFixed(1)}KB`
  );

  return results;
}

// ============================================
// Error Recovery
// ============================================

/**
 * Retry a function with exponential backoff
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelayMs: number = 100
): Promise<T> {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));

      // Don't retry on QuotaExceededError - need cleanup first
      if (lastError.name === 'QuotaExceededError') {
        throw lastError;
      }

      if (attempt < maxRetries) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

// ============================================
// Initialization
// ============================================

/**
 * Initialize the storage manager
 */
export function initStorageManager(): void {
  initBroadcastChannel();

  // Request persistent storage on init
  requestPersistentStorage().then((persisted) => {
    if (persisted) {
      console.warn('[StorageManager] Persistent storage granted');
    }
  });
}

/**
 * Cleanup on app shutdown
 */
export function destroyStorageManager(): void {
  closeBroadcastChannel();
  locks.clear();
}

// Auto-initialize on import
if (typeof window !== 'undefined') {
  initStorageManager();
}
